// API response types matching SharedContracts backend shapes

// ─── Survey Service ────────────────────────────────────────────────────────────

export interface SurveyItem {
  id: string;
  name: string;
  /** Descripción de la encuesta — se muestra bajo el nombre en la tabla del panel (P4-6). */
  description?: string;
  /** Días de la semana en que está activa (0=lunes … 6=domingo). Origen de FRECUENCIA (D-3). */
  activeDays?: number[];
  status: 'active' | 'paused' | 'inactive' | 'pending_approval' | 'cancelled';
  audience: string;
  /** IDs crudos de OU objetivo (o ['global'] para audiencia global) — sin resolver a nombre. */
  ouIds?: string[];
  questions: number;
  responses: number;
  period: string;
  periodStart?: string;
  periodEnd?: string;
  audienceType?: string;
  createdBy?: string;
  isApproved?: boolean;
  deletedAt?: string;
  deletedBy?: string;
  category?: string;
}

export type SurveyListResponse = SurveyItem[];

export interface SurveyQuestionApiDto {
  id: string;
  order: number;
  questionType: string;
  dailyHabitId: string | null;
  fundamentalId: string | null;
  text: Record<string, string>;
  focusType?: string | null;
  presentationMode?: string | null;
  sourceLang?: string;
}

export interface PagedSurveyDto {
  id: string;
  name: string;
  description?: string;
  status: string;
  type: string;
  // RN-009: Audience contract
  audienceType: string;
  ouIds: string[];
  targetUserIds?: string[];
  targetMeetingId?: string;
  isApproved: boolean;
  createdBy: string;
  createdAt: string;
  expiresAt?: string;
  questions: SurveyQuestionApiDto[];
  deletedAt?: string;
  deletedBy?: string;
  category?: string;
  startDate?: string;
  campaignWeight?: number;
  activeDays?: number[];
  boostUrgente?: boolean;
  boostDays?: number;
  minRespondents?: number;
  maxRespondents?: number;
  nameTranslations?: Record<string, string>;
  descriptionTranslations?: Record<string, string>;
  responseCount?: number;
}

export interface PagedSurveyResponse {
  items: PagedSurveyDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export type SurveyType = 'Behaviors' | 'NPS' | 'Open' | 'BehaviorsAndNPS';

export interface CreateSurveyQuestionInput {
  order: number;
  questionType: string;
  dailyHabitId?: string;
  fundamentalId?: string;
  text: Record<string, string>;
  focusType?: string;
  presentationMode?: string;
  sourceLang?: string;
}

export interface TranslateTextsRequest {
  sourceLang: string;
  targetLangs: string[];
  texts: string[];
}

export interface TranslateTextsResponse {
  translations: Record<string, string[]>;
}

export interface CreateSurveyRequest {
  name: string;
  description?: string;
  type: SurveyType;
  // RN-009: Audience contract
  audienceType: 'ou' | 'meeting' | 'people';
  ouIds: string[];
  targetUserIds?: string[];
  targetMeetingId?: string;
  expiresAt?: string;
  questions: CreateSurveyQuestionInput[];
  category?: string;
  startDate?: string;
  campaignWeight?: number;
  activeDays?: number[];
  boostUrgente?: boolean;
  boostDays?: number;
  minRespondents?: number;
  maxRespondents?: number;
  /** Fase 1: señal al backend para auto-activar la encuesta. En producción el backend
   *  valida el rol directamente desde el JWT; este flag es el fallback para dev sin auth. */
  isGlobalAdmin?: boolean;
  nameTranslations?: Record<string, string>;
  descriptionTranslations?: Record<string, string>;
}

export interface CreateSurveyResponse {
  id: string;
  name: string;
  status: string;
  version: number;
  createdBy: string;
  createdAt: string;
}

export interface PendingInstance {
  id: string;
  meetingTitle: string;
  meetingDate: string;
  durationMinutes: number;
  participants: string[];
  expiresAt: string;
}

export interface ParticipantSnapshotDto {
  id: string;
  displayName: string;
  email?: string;
  jobTitle?: string;
  ouId?: string;
  hierarchyPath?: string;
  generation?: string;
  age?: number;
  preferredLanguage?: string;
}

/** Shape real del backend (SurveyInstanceDto serializado) */
export interface SurveyInstanceApiDto {
  id: string;
  surveyId: string;
  surveyName: string;
  status: number;
  evaluatorId: string;
  meetingId: string;
  meetingSubject: string;
  createdAt: string;
  expiresAt: string;
  respondedAt: string | null;
  questions: SurveyQuestionApiDto[];
  participants: ParticipantSnapshotDto[];
}

export interface PendingInstancesPagedResponse {
  items: SurveyInstanceApiDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasNextPage: boolean;
  totalPages: number;
}

export type PendingInstancesResponse = PendingInstancesPagedResponse;

export interface SubmitResponseBody {
  instanceId: string;
  answers: Record<string, string | number>;
  skipped?: boolean;
}

export interface SubmitResponseResult {
  success: boolean;
  instanceId: string;
}

// ─── Analytics Service ─────────────────────────────────────────────────────────

/**
 * Dimensión por la que reporta la analítica: un comportamiento (Actitud y Comportamiento,
 * nivel 2 del catálogo) cuando el fundamental los tiene configurados, o el propio fundamental
 * cuando no. Todas las pestañas comparten esta unidad para que sus cifras coincidan.
 */
export interface DimensionScore {
  id: string;
  name: string;
  /** true = la fila es un comportamiento; false = es un fundamental sin comportamientos. */
  isBehavior: boolean;
  fundamentalId: string;
  fundamentalName: string;
  color?: string | null;
  score: number;
  /** Encuestas que evaluaron esta dimensión. */
  evaluations: number;
  /** Encuestas recibidas en el alcance que no evaluaron esta dimensión. */
  noObs: number;
  /** Opiniones por nivel; índices 0..3 corresponden a los niveles 1..4. */
  distribution: number[];
}

/** Puntaje de una unidad organizacional. El nombre se resuelve con el catálogo de OUs del cliente. */
export interface OuScore {
  ouId: string;
  score: number;
  participantCount: number;
  evaluations: number;
}

/** Omisiones de una unidad organizacional (ver OuOmissionDto en AnalyticsService). */
export interface OuOmission {
  ouId: string;
  peopleCount: number;
  /** Encuestas cerradas: respondidas + omitidas + expiradas. */
  evaluations: number;
  omittedCount: number;
  /** Porcentaje 0–100 de omisión sobre las encuestas cerradas de la unidad. */
  omissionRate: number;
}

export interface AnalyticsSummaryResponse {
  totalParticipants: number;
  /** Encuestas respondidas recibidas. Definición canónica de "evaluación" en toda la analítica. */
  totalEvaluations: number;
  overallAverage: number;
  /** Porcentaje 0–100 de encuestas contestadas sobre las que ya cerraron. */
  responseRate: number;
  avgPerDay: number;
  uniqueEvaluators: number;
  dimensions: DimensionScore[];
  topBehaviors: DimensionScore[];
  opportunities: DimensionScore[];
  scoreByOu: OuScore[];
  omissionsByUnit: OuOmission[];
}

// ─── Analytics Service (detail endpoints) ─────────────────────────────────────

export interface DailyHabitScore {
  id: string;
  name: string;
  score: number;
  evaluations: number;
}

/**
 * Puntaje de una Actitud y Comportamiento (nivel 2 del catálogo): el promedio de los
 * hábitos diarios que cuelgan de ella. Es el nivel que nombra una conducta concreta y por
 * eso es el que se reporta cuando el fundamental lo tiene configurado.
 */
export interface BehaviorScore {
  id: string;
  name: string;
  order: number;
  score: number;
  /** Encuestas que evaluaron este comportamiento. */
  evaluations: number;
  /** Encuestas recibidas en el alcance que no lo evaluaron. */
  noObs: number;
  /** Opiniones por nivel; índices 0..3 corresponden a los niveles 1..4. */
  distribution: number[];
  dailyHabits: DailyHabitScore[];
}

export interface FundamentalScore {
  id: string;
  name: string;
  order?: number;
  score: number;
  evaluations: number;
  noObs: number;
  distribution: number[];
  color?: string | null;
  /** Vacío cuando el fundamental tiene comportamientos: ahí los hábitos cuelgan de ellos. */
  dailyHabits: DailyHabitScore[];
  /** Vacío cuando los hábitos del fundamental cuelgan directo, sin nivel intermedio. */
  behaviors: BehaviorScore[];
}
export type FundamentalsResponse = FundamentalScore[];

/** Columna del mapa de calor: comportamiento o fundamental, según lo configurado en el catálogo. */
export interface HeatmapColumn {
  id: string;
  name: string;
  isBehavior: boolean;
  fundamentalId: string;
  fundamentalName: string;
  color?: string | null;
}

export interface HeatmapRowDto {
  evaluatedId: string;
  /** OU del participante, para poder agrupar el heatmap por OU en el frontend. */
  ouId?: string;
  /** Promedio por dimensión, alineado con `HeatmapResponse.columns`. `null` = sin
   *  evaluaciones para esa dimensión; no confundir con un puntaje real de 0 (ver E-005). */
  scores: (number | null)[];
  /** Encuestas respondidas que recibió la persona en el rango consultado. */
  evaluations: number;
}

export interface HeatmapResponse {
  columns: HeatmapColumn[];
  rows: HeatmapRowDto[];
}

/** Arista dirigida evaluador→evaluado con conteo de interacciones (ver OnaEdgeDto en AnalyticsService). */
export interface OnaEdgeDto { evaluatorId: string; evaluatedId: string; interactionCount: number; }
export interface OnaResponse { edges: OnaEdgeDto[]; }

/** Promedio de una dimensión para una banda generacional (ver GenerationalDimensionDto en AnalyticsService). */
export interface GenerationalDimensionAverage {
  id: string;
  name: string;
  isBehavior: boolean;
  fundamentalId: string;
  fundamentalName: string;
  average: number;
}
/** Grupo generacional agregado (ver GenerationalGroupDto en AnalyticsService — GET /v1/analytics/generational). */
export interface GenerationalGroup {
  generationBand: string;
  participantCount: number;
  overallAverage: number;
  /** Encuestas respondidas recibidas por la banda. */
  evaluations: number;
  dimensions: GenerationalDimensionAverage[];
}
export type GenerationalResponse = GenerationalGroup[];

/** Fase A.3 — participantes individuales por banda generacional (GET /v1/analytics/generational/participants). */
export interface GenerationalParticipant {
  evaluatedId: string;
  ouId: string;
  generationBand: string;
  overallAverage: number;
  responseCount: number;
}
export type GenerationalParticipantsResponse = GenerationalParticipant[];

export interface BiasItem { evaluatorId: string; evaluatorName: string; avgGiven: number; avgReceived: number; delta: number; }
export type BiasResponse = BiasItem[];

/** Persona del ranking de omisiones: `omittedCount` es el número de encuestas que omitió
 *  (dato real de survey_instances) y `responseCount` las respuestas que ha recibido como
 *  evaluada (ver ParticipantOmissionDto en AnalyticsService; no incluye nombre de usuario). */
export interface ParticipantOmissionDto {
  evaluatedId: string;
  ouId: string;
  responseCount: number;
  omittedCount: number;
  lastUpdatedAt: string;
}
export interface OmissionsResponse {
  totalParticipants: number;
  participantsWithZeroResponses: number;
  participants: ParticipantOmissionDto[];
}

/**
 * Puntaje de una Actitud y Comportamiento en el dashboard personal (ver PersonalAttitudeDto).
 * Este endpoint mantiene su propio contrato porque distingue `evaluations` (puntajes de daily
 * habit sumados) de `responses` (personas que respondieron), distinción que la analítica
 * agregada no necesita.
 */
export interface AttitudeScore {
  id: string;
  name: string;
  order: number;
  score: number;
  /** Volumen de puntajes de daily habit que componen el promedio; no es número de personas. */
  evaluations: number;
  /** Número real de personas que respondieron por este comportamiento; úsese para todo lo
   *  que se rotule como "colegas" o gobierne una decisión de confianza. */
  responses: number;
  /** Opiniones por nivel; índices 0..3 corresponden a los niveles 1..4. */
  distribution?: number[];
  dailyHabits: DailyHabitScore[];
}

/** Fundamental dentro del dashboard personal (ver PersonalFundamentalDto en AnalyticsService). */
export interface PersonalFundamentalScore {
  id: string;
  name: string;
  order?: number;
  score: number;
  evaluations: number;
  noObs: number;
  distribution: number[];
  dailyHabits: DailyHabitScore[];
  /** Vacío cuando los hábitos del fundamental cuelgan directo, sin nivel intermedio. */
  attitudes: AttitudeScore[];
}

export interface PersonalResponse {
  evaluatedId: string;
  avgScore: number;
  trend: number;
  fundamentalScores: PersonalFundamentalScore[];
  /** Respuestas recibidas que alimentan el agregado. */
  responseCount: number;
  lastUpdatedAt: string;
}

/** Encuesta que la persona ya respondió, para el historial de retroalimentación entregada. */
export interface AnsweredInstance {
  instanceId: string;
  surveyName: string;
  meetingSubject: string;
  participantCount: number;
  respondedAt: string;
}

// ─── User Admin Service ────────────────────────────────────────────────────────

/**
 * Permisos granulares que el AdminGlobal activa por usuario en la pantalla de roles
 * (UsersAdminView). Determinan qué secciones del panel de administración ve un AdminOU.
 */
export interface AdminPermissions {
  canCreateSurveys: boolean;
  canViewAnalytics: boolean;
  canManageUsers: boolean;
  canApproveSurveys: boolean;
  /**
   * Acceso a los catálogos maestros del sistema (Admin General). Aditivos 2026-08-08: OPCIONALES
   * para no romper los literales previos de `AdminPermissions`; ausente ⇒ sin acceso (fail-safe).
   * OJO: el PATCH `/permissions` reescribe el conjunto completo, así que todo emisor debe enviar
   * los seis flags (la pantalla Roles y Permisos de Pulso lo hace); un PATCH parcial los apagaría.
   */
  canAccessCatalogFundamentals?: boolean;
  canAccessCatalogOus?: boolean;
}

/**
 * Alcance organizacional de un administrador, tal como se configura en la pantalla de roles.
 * Determina de qué personas puede ver reportes: las de sus OUs raíz y todos los niveles
 * inferiores de esas ramas del árbol. `allOusAccess` (opción "Todas") levanta la restricción.
 */
export interface OuScope {
  /** OUs raíz del alcance — uno o varios (RN-R07: cascada a descendientes ya resuelta server-side). */
  rootOuIds: string[];
  allOusAccess: boolean;
}

/**
 * Visibilidad de los dos módulos del producto. Es un eje independiente del rol: un empleado
 * externo puede tener acceso a un módulo y no al otro. Todos los usuarios sincronizados desde
 * AD tienen Pulso activo (ver migración 2026-07-28).
 */
export interface UserModules {
  canViewPulso: boolean;
  canViewBrillamos: boolean;
}

/** Shape returned by UserAdmin API (matches UserDto from backend) */
export interface UserItem {
  /**
   * REQ 3 - true si su fecha de ingreso es FUTURA y por tanto todavia no puede entrar.
   *
   * Es la DECISION, no la fecha: /v1/users/resolve es anonimo, asi que publicar la fecha de
   * contratacion permitiria averiguarla de cualquiera con solo su correo.
   */
  accessPendingHireDate?: boolean;
  id: string;
  /**
   * 🔴 ANULABLE. El comentario de arriba decia "matches UserDto from backend" y habia dejado de
   * ser cierto: desde el padron Frontline hay ~3 930 fichas sin correo (el colaborador de planta
   * no tiene cuenta corporativa). Declararlo `string` no lo hacia no-nulo, solo apagaba al
   * compilador donde mas falta hacia.
   */
  email: string | null;
  displayName?: string;
  jobTitle?: string;
  ouId?: string;
  ouName?: string;
  /**
   * Área del colaborador — Nivel 4 de la jerarquía, `department` de Graph (`UserDto.Department`
   * del backend). Es la fuente del chip de área de la tarjeta de perfil (`.rp2-meta-chip`).
   */
  department?: string;
  /**
   * URL de la foto de perfil sincronizada desde Azure AD/Entra ID (Graph `/photo/$value`).
   * PENDIENTE de implementar en backend — ver PENDIENTES.md. Mientras el campo no llegue, el
   * frontend cae siempre al avatar de iniciales (comportamiento actual, sin cambios).
   */
  photoUrl?: string | null;
  tenantId: string;
  roles: string[];
  preferredLanguage?: string;
  isActive: boolean;
  canCreateSurveys?: boolean;
  canViewAnalytics?: boolean;
  canManageUsers?: boolean;
  canApproveSurveys?: boolean;
  canAccessCatalogFundamentals?: boolean;
  canAccessCatalogOus?: boolean;
  allOusAccess?: boolean;
  canViewPulso?: boolean;
  canViewBrillamos?: boolean;
  /**
   * Permisos canónicos del módulo Reconocimiento (`UserDto.Permissions` en el backend,
   * `PermissionKeys.Order` en SharedContracts). Sin relación con los cuatro `can*` de arriba,
   * que son el legado del módulo Pulso.
   */
  permissions?: string[];
  /**
   * Alcance de administración sobre Pulso (`UserDto.PulsoAdminScopeOuIds`) — ya expandido con
   * descendencia. Distinto de `ouId`: `ouId` es la OU de pertenencia del usuario, este campo es
   * qué OUs administra si su rol es AdminOU.
   */
  pulsoAdminScopeOuIds?: string[];
  /**
   * Alcance de administración sobre Reconocimiento/Brillamos (`UserDto.RecognitionScopeOuIds`) —
   * ya expandido con descendencia (RN-R07, mismo criterio que `pulsoAdminScopeOuIds`). Distinto de
   * ese campo: un AdminOUBrillamos no administra Pulso, así que mezclarlos filtraría el alcance
   * equivocado.
   */
  recognitionScopeOuIds?: string[];
}

/** Request body for POST /v1/users */
export interface CreateUserApiRequest {
  email: string;
  displayName?: string;
  roles?: string[];
  /** Object Id de Azure AD ya conocido (viene de la selección del autocomplete de usuarios).
   *  Si se envía, el backend sincroniza por id directo en vez de resolver el correo contra Graph. */
  aadObjectId?: string;
}

export interface UserListResponse {
  items: UserItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface OUItem { id: string; name: string; description?: string; adminUserId?: string; isActive: boolean; gdpr?: boolean; type?: 'OU' | 'BU'; timezone?: string; azureAdGroupId?: string; parentOuId?: string | null; createdAt?: string; updatedAt?: string; }
export type OUListResponse = OUItem[];

export interface OUTreeNode { id: string; name: string; children: OUTreeNode[]; }
export type OUTreeResponse = OUTreeNode;
/** Raíces reales descubiertas en Azure AD vía Graph (sin relación directa con las OUs de Mongo). */
export type OURootsResponse = OUTreeNode[];

/** Estado de la caché del catálogo de OUs (catalog:ous:{tenantId}) — ver GET /v1/organizational-units/cache-status. */
export interface OuCatalogCacheStatusDto {
  isPopulated: boolean;
  ouCount: number;
  lastSyncedAt: string | null;
}

// ─── Jobs de sync AD (monitoreo, GET/POST /v1/jobs) ────────────────────────────

export type JobName =
  | 'OuCatalogSync'
  | 'UserCatalogSync'
  | 'SuccessFactorsUserSync'
  | 'SuccessFactorsOuSync'
  | 'AdDomainSweep';
export type JobTriggerType = 'Scheduled' | 'Manual';
export type JobRunStatus = 'Success' | 'Skipped' | 'Failed';

export interface JobRunDto {
  id: string;
  jobName: JobName;
  triggerType: JobTriggerType;
  triggeredByUserId: string | null;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  status: JobRunStatus;
  created: number | null;
  updated: number | null;
  unchanged: number | null;
  skipReason: string | null;
  errorMessage: string | null;

  // Contadores del sync de SuccessFactors. Aditivos: llegan en null para los jobs del AD, que no
  // tienen nada que contar ahi.
  /** Filas leidas del feed. */
  received?: number | null;
  /** Descartadas por no traer empleo. NO es un error: son ~77 260 filas de PerPerson. */
  sinEmpleo?: number | null;
  /** Corporativos saltados por correr en AdOnly. */
  skippedByMode?: number | null;
  /** Bajas que no existen en la base y no se crean. */
  ignored?: number | null;
  deactivatedExplicit?: number | null;
  deactivatedAbsent?: number | null;
  /** Total esperado segun la verificacion de conteo previa. */
  expectedCount?: number | null;
  /** Nominas DISTINTAS enumeradas. Se compara con expectedCount. */
  distinctSeen?: number | null;

  /** Filas cuya nomina ya habia salido en la corrida (paginacion que repite). */
  duplicateInFeed?: number | null;
  /** Personas que empataron con un documento que ya reclamo otra de la misma corrida. */
  matchConflict?: number | null;
  /** Usuarios que recibieron ManagerId en la 2a pasada. FUERA de la identidad. */
  managerLinksUpdated?: number | null;
  /** Personas dejadas sin correo porque otra ya tenia ese. Distinto de 0 = dato sucio en el ORIGEN. */
  duplicateEmail?: number | null;

  // Contadores del sync del AD de las reglas de licencia y practicantes (oct-2026). Aditivos: llegan en
  // null para los otros jobs, y en 0 con las reglas apagadas.
  /** Cuentas que la regla de licencia dejó fuera, o habría dejado fuera en modo solo reporte. */
  licenseDiscarded?: number | null;
  /** Practicantes que entraron al padrón, o que habrían entrado en modo solo reporte. */
  practicantesIncluded?: number | null;
  /** Practicantes que no entraron porque extensionAttribute12 venía vacío o no era una región válida. */
  practicantesSinRegion?: number | null;
  /** Practicantes dados de baja (borrado lógico), o que se darían de baja en modo solo reporte. */
  practicantesDeactivated?: number | null;
  /** Personas del directorio que perdieron la licencia y pasarían a Frontline (o ya pasan, en modo aplicar). */
  licenseDemotionCandidates?: number | null;
  /** Personas a las que la corrida les puso la marca de «sin licencia». Solo en modo aplicar. */
  licenseDemoted?: number | null;

  // Contadores del BARRIDO DE DOMINIOS (REQ 2 / REQ 5). Llegan en null para los otros cuatro jobs.
  /** Cuentas habilitadas que devolvio el barrido de Entra, invitados incluidos. */
  cuentasBarridas?: number | null;
  /** Dominios distintos observados en esas cuentas. */
  dominiosObservados?: number | null;
  /** Observados que NO estan en el catalogo declarado. Informativo. */
  dominiosNoDeclarados?: number | null;
  /**
   * Declarados que el barrido ya no observa.
   *
   * 🔴 La direccion peligrosa: sacar uno del catalogo ABRE el auto-registro a toda su gente. Sale 0
   * si el barrido no fue representativo, asi que un 0 aqui NO significa "todo bien" cuando la corrida
   * vio pocas cuentas -- por eso se pinta junto a cuentasBarridas.
   */
  dominiosDesaparecidos?: number | null;
  /** Documentos marcados inactivos porque el barrido ya no los vio. Nunca se borran. */
  dominiosDesactivados?: number | null;

  /** Colgaban de un descendiente REAL y subieron a la raiz de su rama. Higiene. */
  ouSubidaARaiz?: number | null;
  /** Su OU YA NO EXISTIA y se resolvio por Region. Se espera que baje a 0 y se quede ahi. */
  ouHuerfanaResuelta?: number | null;
  /** 🔴 OU rota que Region tampoco resolvio. No se borra: se queda para poder arreglarla. */
  ouHuerfanaSinResolver?: number | null;
}

export interface JobSummaryDto {
  jobName: JobName;
  isRunning: boolean;
  lastRun: JobRunDto | null;
  nextScheduledAt: string | null;
}

/** Resultado de la carga masiva Europa/EE. UU. */
export interface EuropeUsaImportErrorDto {
  lineNumber: number;
  column: string;
  message: string;
  /** false = advertencia informativa (la fila se procesa igual); true/ausente = descarta la fila. */
  isBlocking?: boolean;
  /** Código estable opcional (ej. `email-in-use-active`) para traducir el mensaje en el cliente; `message` queda como respaldo en español. */
  code?: string | null;
}
export interface EuropeUsaImportResultDto {
  totalRows: number;
  created: number;
  updated: number;
  deactivated: number;
  skipped: number;
  errors: EuropeUsaImportErrorDto[];
  /** CSV descargable con los errores, para corregir el archivo. */
  errorsCsv: string;
  preview: boolean;
  /** true si la carga esta apagada por configuracion (BulkEuropeUsaEnabled). */
  disabled?: boolean;
  /** Encabezados del archivo que no pertenecen al esquema de 25 columnas — se ignoraron, no bloquearon la carga. */
  ignoredColumns?: string[];
}

/** Cuerpo del PUT de configuracion de sincronizacion. */
/**
 * Interruptor de tres estados de las reglas nuevas del sync del AD. Espeja `SyncRuleMode` del backend: se
 * enciende primero en `ReportOnly` (cuenta a quién afectaría sin tocar a nadie) y después en `Enforce`.
 */
export type SyncRuleMode = 'Off' | 'ReportOnly' | 'Enforce';

/** Ajustes del sync del AD: licencia, inclusión de practicantes y baja de practicantes. */
export interface AdSyncSettingsDto {
  licenseMode: SyncRuleMode;
  /** `SkuId` (GUID de Entra) de las licencias que cuentan: E3, E5, F3 y F1. */
  allowedLicenseSkuIds: string[];
  /** Tope de descartados por licencia, en %: si se rebasa, la corrida no escribe a nadie. */
  maxLicenseDiscardPercent: number;
  practicanteInclusionMode: SyncRuleMode;
  practicanteDeactivationMode: SyncRuleMode;
  /** Tope de bajas de practicantes, en % de los activos: si se rebasa, no se da de baja a nadie. */
  maxPracticanteDeactivationPercent: number;
  /**
   * Baja a Frontline por licencia: a quien el directorio deja fuera por no tener licencia y SuccessFactors sigue
   * trayendo, se le marca «sin licencia» y SuccessFactors lo pasa a Frontline. Solo corre con la licencia en «Aplicar».
   */
  licenseDemotionMode: SyncRuleMode;
  /** Tope de conversiones, en % de las personas del directorio con SuccessFactors: si se rebasa, no se convierte a nadie. */
  maxLicenseDemotionPercent: number;
}

export interface SaveSyncSettingsBody {
  userSource: UserSyncSource;
  ouSource: OuSyncSource;
  bulkEuropeUsaEnabled: boolean;
  /**
   * REQ 2 - dominios que obligan a entrar por SSO en vez de registrarse.
   *
   * Opcional a proposito: el backend entiende "no viene" como "no lo cambies", NUNCA como
   * "vacialo". Un catalogo vacio significaria que nadie tiene correo corporativo, o sea
   * auto-registro abierto para el directorio entero.
   */
  corporateDomains?: string[];
  successFactors: SuccessFactorsSyncSettingsDto;
  /**
   * Opcional a propósito, y es lo que protege la configuración: el backend entiende «no viene» como «no lo
   * cambies». Solo la tarjeta de reglas del AD lo manda; las demás no, para que guardar la pestaña de
   * dominios desde una pantalla desactualizada no pise lo que se cambió después.
   */
  adSync?: AdSyncSettingsDto;
  /** Mismo criterio que `adSync`: solo lo manda la tarjeta de «A nombre de». */
  delegation?: DelegationSettingsDto;
}

/**
 * Regla de «A nombre de» (Función 1): quién puede aparecer como emisor en la caja y en la carga masiva.
 * Arranca apagada; la lista es de familias de puesto EXCLUIDAS (códigos de SuccessFactors).
 */
export interface DelegationSettingsDto {
  restrictionEnabled: boolean;
  excludedJobFunctions: string[];
}

export type JobsListResponse = JobSummaryDto[];

// ─── Configuracion de fuentes de sincronizacion (GET/PUT /v1/admin/sync-settings) ──────

/** Los tres caminos de la junta (N8 / D-U4). */
export type UserSyncSource = 'AdOnly' | 'ApiOnly' | 'AdThenApi';
export type OuSyncSource = 'Ad' | 'Api';

export interface SuccessFactorsSyncSettingsDto {
  /** Arranca APAGADO. */
  enabled: boolean;
  /** Arranca en simulacion: cuenta todo y no escribe nada. */
  dryRun: boolean;
  deactivateAbsent: boolean;
  /** Guardarrail del barrido de ausentes, en % sobre el padron de SuccessFactors. */
  maxAbsentPercent: number;
  /** Lista BLANCA: lo que no este aqui se considera inactivo. */
  activeEmployeeStatuses: string[];
  /** Campos donde SF pisa aunque el AD haya escrito. Arranca vacia. */
  overrideFields: string[];
  /** REQ 1 - funciones del puesto (customString35) que pueden auto-registrarse. */
  selfRegisterJobFunctions: string[];
  /**
   * Partes locales que SuccessFactors usa para decir "esta persona no tiene correo"
   * (no_mail, nomail, sin_correo...). NO son direcciones: se compara solo lo anterior a la @.
   *
   * Medido: `no_mail@sigma-alimentos.com` son 111 personas y `no_mail@gmail.com` 1 -- una lista de
   * direcciones completas se dejaba fuera la segunda, y con dos asi vuelve el choque de indice unico
   * que mato una corrida de 130 000 filas el 2026-08-07.
   */
  emailSentinelLocalParts: string[];
}

export interface SyncSettingsDto {
  userSource: UserSyncSource;
  ouSource: OuSyncSource;
  bulkEuropeUsaEnabled: boolean;
  /** REQ 2 - los 35 dominios que declaro negocio. Ver CATALOGO-DOMINIOS-CORPORATIVOS.md */
  corporateDomains: string[];
  successFactors: SuccessFactorsSyncSettingsDto;
  /** Opcional: un backend anterior al cambio de licencia y practicantes no lo devuelve. */
  adSync?: AdSyncSettingsDto;
  /** Opcional: un backend anterior a la Función 1 no lo devuelve. */
  delegation?: DelegationSettingsDto;
  updatedAt: string;
  updatedBy: string | null;
}
export type JobRunsListResponse = JobRunDto[];

/** Slugs de ruta usados por GET/POST /v1/jobs/{jobName}/... — ver JobsController (backend). */
export type JobSlug =
  | 'ou-catalog-sync'
  | 'user-catalog-sync'
  | 'sf-user-sync'
  | 'sf-ou-sync'
  | 'ad-domain-sweep';

// ─── Rules Engine ─────────────────────────────────────────────────────────────

export interface RuleOuOverrideItem { ouId: string; value: string; }
export interface RuleItem { code: string; description: string; value: string; isActive: boolean; ouOverrides?: RuleOuOverrideItem[]; }
export type RulesListResponse = RuleItem[];

// ─── Personal Growth (AnalyticsService) ───────────────────────────────────────

export interface FundamentalSnapshotItem {
  fundamentalId: string;
  average: number;
  evaluations: number;
}

/** Promedio mensual de un comportamiento; alimenta su historial en el detalle. */
export interface AttitudeSnapshotItem {
  attitudeId: string;
  average: number;
  evaluations: number;
}

export interface GrowthSnapshotItem {
  month: string;           // "YYYY-MM"
  overallAverage: number;
  /** Respuestas recibidas en el mes; da contexto al promedio. */
  evaluations: number;
  fundamentals: FundamentalSnapshotItem[];
  attitudes: AttitudeSnapshotItem[];
}

/**
 * Ventana móvil de 30 días. Sin respuestas en el rango, todos los campos numéricos
 * llegan en cero — nunca `null` — así que el frontend no necesita distinguir "sin datos"
 * de "todo en cero".
 */
export interface GrowthWindow {
  startDate: string;
  endDate: string;
  overallAverage: number;
  evaluations: number;
  fundamentals: FundamentalSnapshotItem[];
  attitudes: AttitudeSnapshotItem[];
}

export interface PersonalGrowthResponse {
  evaluatedId: string;
  history: GrowthSnapshotItem[];
  /** Ventana [hoy-30d, hoy). Alimenta la araña, la narrativa de perfil y la flecha de tendencia. */
  today: GrowthWindow;
  /** Ventana equivalente tres meses atrás: [hoy-120d, hoy-90d). */
  threeMonthsAgo: GrowthWindow;
}

// ─── My Stats (SurveyService) ─────────────────────────────────────────────────

export interface MyStatsResponse {
  totalAnswered: number;
  totalOmitted: number;
  streak: number;
  participationRate: number;
}

// ─── Tips (SurveyService) ─────────────────────────────────────────────────────

export interface TipItem {
  id: string;
  fundamentalId: string;
  operator: '>=' | '<' | 'between';
  threshold: number;
  thresholdMax: number | null;
  tipType: 'reconocer' | 'crecer';
  tipName: string;
  tipText: string;
  translations: Record<string, string>;
  active: boolean;
}
export type TipsResponse = TipItem[];

// ─── Actions (SurveyService) ──────────────────────────────────────────────────

export interface ActionTranslationItem { title: string; description: string; nextLevelText: string; }
export interface ActionItem {
  id: string;
  fundamentalId: string;
  threshold: number;
  thresholdMax: number;
  title: string;
  description: string;
  nextLevelText: string;
  translations: Record<string, ActionTranslationItem>;
  timeSeconds: number;
  xp: number;
  active: boolean;
}
export type ActionsResponse = ActionItem[];

// ─── Notifications (NotificationService) ─────────────────────────────────────

export interface OuOverrideDto {
  ouId: string;
  language: string;
  userMessage: string;
  managerMessage: string;
}

export interface NotificationSettingsDto {
  omitirEnabled: boolean;
  omitirThreshold: number;
  omitirNotifyUser: boolean;
  omitirNotifyManager: boolean;
  omitirNotifyAdminOU: boolean;
  omitirUserTemplates: Record<string, string>;
  omitirMgrTemplates: Record<string, string>;
  europaEnabled: boolean;
  omitirOuOverrides: OuOverrideDto[];
  europaUserTemplates: Record<string, string>;
  europaMgrTemplates: Record<string, string>;
  generalOuOverrides: OuOverrideDto[];
}

export interface SendGeneralNotificationRequest {
  subject: string;
  body: string;
  recipientEmails: string[];
  audienceOuId?: string;
}

export interface SendGeneralNotificationResult {
  recipientCount: number;
  successCount: number;
}

// ─── Catalog (SurveyService) ──────────────────────────────────────────────────

export interface AttitudeBehaviorCatalogItem {
  id: string;
  fundamentalId: string;
  code: string;
  order: number;
  names: Record<string, string>;
  isActive: boolean;
  image?: string;
  description?: string;
  descriptions?: Record<string, string>;
}

export interface DailyHabitCatalogItem {
  id: string;
  fundamentalId: string;
  code: string;
  order: number;
  names: Record<string, string>;
  questionIntro: Record<string, string>;
  isActive: boolean;
  attitudeBehaviorId?: string;
  image?: string;
}

export interface FundamentalCatalogItem {
  id: string;
  code: string;
  order: number;
  names: Record<string, string>;
  dailyHabits: DailyHabitCatalogItem[];
  isActive: boolean;
  color?: string;
  shortName?: string;
  shortNames?: Record<string, string>;
  description?: string;
  descriptions?: Record<string, string>;
  icon?: string;
  url?: string;
  micrositeUrl?: string;
  image?: string;
  attitudes: AttitudeBehaviorCatalogItem[];
}

export type FundamentalCatalogResponse = FundamentalCatalogItem[];

// ─── Templates (SurveyService) ────────────────────────────────────────────────

export interface TemplateItem {
  id: string;
  name: string;
  description?: string;
  questionCount: number;
  category?: string;
  createdBy: string;
  usesCount: number;
  createdAt: string;
  expiresAt?: string;
  isTemplate: boolean;
}
export interface TemplateListResponse {
  items: TemplateItem[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Survey Export (SurveyService) ─────────────────────────────────────────────

export interface SurveyExportGeneral {
  name: string;
  description?: string;
  type: string;
  status: string;
  ouIds: string[];
  responseCount: number;
  responseRate: number;
  expiresAt?: string;
  isApproved: boolean;
  createdBy: string;
  createdAt: string;
}

export interface SurveyExportQuestion {
  order: number;
  text: string;
  type: string;
  fundamentalId?: string;
  dailyHabitId?: string;
}

export interface SurveyExportFeedback {
  evaluatorId: string;
  evaluatedDisplayName?: string;
  respondedAt: string;
  questionText: string;
  answer: string;
}

export interface SurveyExportData {
  generalInfo: SurveyExportGeneral;
  questions: SurveyExportQuestion[];
  feedbacks: SurveyExportFeedback[];
}

// ─── Trazabilidad del pipeline (AdminGlobal) ─────────────────────────────────

/** Desenlace de una etapa. 'NotEligible' es un resultado esperado del negocio, no una falla. */
export type TraceOutcome = 'Ok' | 'Error' | 'NotEligible' | 'NoData';

/** Nombre estable de cada etapa; la UI lo traduce con la clave `trace.stage.<nombre>`. */
export type TraceStageName =
  | 'MeetingEnd' | 'MeetingEligibility' | 'ParticipantEligibility'
  | 'SurveyCreation' | 'Notification';

export interface TraceStage {
  order: number;
  name: TraceStageName;
  service: string;
  timestamp: string | null;
  outcome: TraceOutcome;
  summary: string;
  details: string[];
}

export interface TraceMeeting {
  id: string;
  subject: string;
  organizerId: string;
  tenantId: string;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  durationMinutes: number | null;
  correlationId: string | null;
  isRecurring: boolean;
  attendanceReportAppliedAt: string | null;
  attendanceReportAttempts: number;
  calendarMatchedExactly: boolean | null;
}

export interface TraceParticipant {
  userId: string;
  displayName: string | null;
  email: string | null;
  ouId: string | null;
  timezone: string;
  surveysToday: number;
  surveysThisWeek: number;
  hoursSinceLastEligibleMeeting: number | null;
  isEligible: boolean;
  reason: string | null;
  includedInSample: boolean;
  samplingReason: string | null;
}

export interface TraceInstance {
  id: string;
  surveyName: string;
  evaluatorId: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  respondedAt: string | null;
}

export interface TraceNotification {
  id: string;
  channel: string;
  notificationType: string;
  recipientId: string;
  recipientEmail: string;
  status: string;
  createdAt: string;
  sentAt: string | null;
  errorMessage: string | null;
  surveyInstanceId: string | null;
}

export interface TraceLogEntry {
  timestamp: string | null;
  service: string;
  level: string | null;
  message: string | null;
  exception: string | null;
}

export interface PipelineTrace {
  meeting: TraceMeeting;
  stages: TraceStage[];
  participants: TraceParticipant[];
  surveyInstances: TraceInstance[];
  notifications: TraceNotification[];
  rawLogs: TraceLogEntry[];
  warnings: string[];
  highlightUserId: string | null;
}

export interface TraceMeetingMatch {
  meetingId: string;
  subject: string;
  organizerId: string;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
}

/** Motivo por el que una búsqueda no llegó a ninguna reunión. */
export type TraceNotFoundReason =
  | 'EmailNotRegistered' | 'UserWithoutMeetings' | 'SubjectNoMatches' | 'MeetingNotFound'
  | 'UserDirectoryUnavailable';

export interface TraceResponse {
  trace: PipelineTrace | null;
  matches: TraceMeetingMatch[];
  notFoundReason: TraceNotFoundReason | null;
}

/**
 * Filtros de búsqueda de la traza. Sin `tenantId`: el backend lo resuelve del token del
 * llamador e ignora cualquier valor que llegue por query (cierre de fuga de datos entre
 * tenants, ver GET /v1/diagnostics/trace).
 */
export interface TraceQueryParams {
  meetingId?: string;
  email?: string;
  meetingSubject?: string;
}

// ─── Gestión de usuarios Frontline (Admin General) ──────────────────────────────────────────────
// Espejo de `PMP.SharedContracts.DTOs.Users.FrontlineUserAdminDto` y relacionados. El backend serializa
// camelCase y los enums como TEXTO (`JsonStringEnumConverter` en UserAdminService).

/** Estado de verificación del correo, derivado de la ficha en el backend (no se guarda). */
export type FrontlineVerificationState = 'Verificado' | 'Pendiente' | 'NoEnviado' | 'SinCorreo' | 'DelDirectorio';

export const FRONTLINE_VERIFICATION_STATES: readonly FrontlineVerificationState[] =
  ['Verificado', 'Pendiente', 'NoEnviado', 'SinCorreo', 'DelDirectorio'];

export type FrontlineVerificationEventType =
  | 'VerificacionEnviada' | 'VerificacionReenviada' | 'CorreoVerificadoPorUsuario' | 'CorreoActualizado'
  | 'VerificadoManualmente' | 'EnlaceRestablecimientoEnviado' | 'PasswordTemporalGenerada';

export interface FrontlineUserAdminDto {
  id: string;
  nomina: string | null;
  nombre: string;
  /** Correo de SuccessFactors, solo lectura. Puede venir vacío. */
  correoCorporativo: string | null;
  /** El correo de acceso de la cuenta Frontline. Vacío si no tiene. */
  correoPersonal: string | null;
  ouId: string | null;
  ou: string | null;
  sitioId: string | null;
  sitio: string | null;
  estado: FrontlineVerificationState;
  ultimoEnvioVerificacion: string | null;
  ultimoCambioPassword: string | null;
  /** La persona ya tiene su cuenta. Una ficha sin reclamar no se puede verificar a mano. */
  reclamada: boolean;
  /** Vencimiento de una contraseña temporal SIN USAR todavía vigente; `null` si no hay. Nunca la contraseña. */
  contrasenaTemporalVenceEn: string | null;
  /** Estatus de la cuenta: `false` = dada de baja. */
  activo: boolean;
  /** Perdió la licencia: pasó a Frontline y tiene que registrarse. Opcional: un servicio anterior no lo manda. */
  sinLicencia?: boolean;
}

export interface FrontlineUsersSummaryDto {
  verificados: number;
  pendientes: number;
  noEnviados: number;
  sinCorreo: number;
  /** Cuentas que vienen del AD (solo lectura): se cuentan APARTE de las otras cuatro. */
  delDirectorio: number;
}

export interface FrontlineUsersAdminPageDto {
  items: FrontlineUserAdminDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: FrontlineUsersSummaryDto;
}

/** Filtros del listado y de la exportación (la exportación no pagina). */
/** Staff o Frontline como los cuentan los reportes: por el dominio del correo (Staff = dominio de Sigma). */
export type FrontlineSegment = 'Staff' | 'Frontline';
export const FRONTLINE_SEGMENTS: FrontlineSegment[] = ['Staff', 'Frontline'];

export interface FrontlineUsersFilters {
  search?: string;
  ouId?: string;
  siteId?: string;
  segment?: FrontlineSegment;
  state?: FrontlineVerificationState;
  /** `true` = solo activas (interruptor «ocultar inactivas»); ausente = todas. */
  isActive?: boolean;
}

export interface FrontlineVerificationEventDto {
  tipo: FrontlineVerificationEventType;
  ocurridoEn: string;
  actorId: string | null;
  actorNombre: string | null;
  detalle: string | null;
}

export interface FrontlineVerificationSendResultDto {
  tipo: FrontlineVerificationEventType;
  enviadoA: string;
}

export interface FrontlineUserStateDto {
  correoPersonal: string | null;
  estado: FrontlineVerificationState;
  cambio: boolean;
}

/**
 * La contraseña temporal recién generada. 🔴 `password` viaja en claro UNA sola vez: no se guarda, no se registra y se
 * descarta al cerrar el modal. `expiresAt` es `null` si no se pidió cambio obligatorio (la contraseña no vence).
 */
export interface TemporaryPasswordDto {
  password: string;
  expiresAt: string | null;
  forceChange: boolean;
}

export interface FrontlinePasswordResetSendResultDto {
  enviadoA: string;
}

/** Resultado de la exportación: el archivo y si quedó incompleto por el tope de filas. */
export interface FrontlineUsersExportResult {
  blob: Blob;
  truncated: boolean;
}
